import { z } from "zod";

// A point-in-time body-measurement snapshot. Special-category health data
// (GDPR Art. 9 / nLPD): never aggregated, never logged, wiped on account
// delete — same posture as Profile.bodyweightKg.
//
// Stored as its own DDB row (pk=USER#<sub>, sk=MEASUREMENT#<13-digit-ms>#<id>),
// a time-series entity rather than an embedded Profile array. The keying
// mirrors the Session envelope (see ddbKeys.sessionSk): a date-window read
// is an O(window) range query, and a future coach cross-member view can
// fetch a member's history without rehydrating their whole Profile.
//
// Units: every circumference + height is centimetres; weight is kilograms
// (the app-wide canonical, see units.ts). The US Navy body-fat / lean-mass
// readout is DERIVED at render from {sex, height, neck, waist, hips} via
// navy.ts — never persisted here (no denormalised snapshots; a corrected
// entry must recompute).

// cm circumference: 1 cm floor rules out a fat-finger 0; 300 cm ceiling is
// far past any human limb/torso and catches a mm-entered-as-cm slip.
const Circumference = z.number().min(1).max(300);
const HeightCm = z.number().min(50).max(260);
// Mirror Profile.bodyweightKg bounds so the canonical weight stays in one
// validated band wherever it's written.
const WeightKg = z.number().min(20).max(300);

// Body-composition fields imported via Health Connect (slice 2). MEASURED
// values (≠ JL's Navy estimate, which stays DERIVED at render from
// circumferences). The upstream device is unknown to JL (smart scale, or a
// manual entry in another HC-connected app). They carry their own `source` so the two
// coexist as separate curves on one chart. BIA absolutes are noisy — the UI
// treats fat/lean as labelled trend, never fed into algorithms.
const BodyFatPct = z.number().min(2).max(70);
const LeanMassKg = z.number().min(10).max(200);
const BoneMassKg = z.number().min(0.5).max(10);
const BodyWaterKg = z.number().min(5).max(150);
const BmrKcalDay = z.number().int().min(500).max(5000);

export const Measurement = z.object({
  id: z.uuid(),
  // Partition-key data, included so a single-row read is self-describing.
  // Always equals the row's pk USER#<sub>.
  userSub: z.string().min(1).max(128),
  // When the measurement was taken (drives the sk + chronological order).
  takenAt: z.iso.datetime(),
  // All measurement fields optional: an entry can be a single weigh-in or
  // a full panel. An entry with no fields at all is still valid (a dated
  // placeholder) but the UI won't offer to save an empty one.
  weightKg: WeightKg.optional(),
  heightCm: HeightCm.optional(),
  neckCm: Circumference.optional(),
  chestCm: Circumference.optional(),
  waistCm: Circumference.optional(),
  hipsCm: Circumference.optional(),
  // Per-side limb girths (left / right) — SL kept both sides; asymmetry is
  // a signal worth tracking.
  bicepsLCm: Circumference.optional(),
  bicepsRCm: Circumference.optional(),
  forearmsLCm: Circumference.optional(),
  forearmsRCm: Circumference.optional(),
  thighsLCm: Circumference.optional(),
  thighsRCm: Circumference.optional(),
  calvesLCm: Circumference.optional(),
  calvesRCm: Circumference.optional(),
  // Measured body composition (smart scale via Health Connect). Distinct
  // from the Navy estimate (derived at render). See next/health-connect-cgm.md.
  bodyFatPct: BodyFatPct.optional(),
  leanMassKg: LeanMassKg.optional(),
  boneMassKg: BoneMassKg.optional(),
  bodyWaterKg: BodyWaterKg.optional(),
  bmrKcalDay: BmrKcalDay.optional(),
  note: z.string().max(500).optional(),
  // Provenance for multi-source body data. Absent ⇒ JL-entered. e.g.
  // "hc:com.huami.watch.hmwatchmanager" for a scale reading via HC.
  // `externalId` = HC record id (dedup/idempotency). Set on import, not patched.
  source: z.string().min(1).max(96).optional(),
  externalId: z.string().min(1).max(256).optional(),
  createdAt: z.iso.datetime(),
  updatedAt: z.iso.datetime(),
});
export type Measurement = z.infer<typeof Measurement>;

// Client → server payload. Server fills userSub/createdAt/updatedAt. id is
// optional but a UUID when present (offline-first idempotency, same pattern
// as NewSession).
export const NewMeasurement = Measurement.omit({
  id: true,
  userSub: true,
  createdAt: true,
  updatedAt: true,
}).extend({ id: z.uuid().optional() });
export type NewMeasurement = z.infer<typeof NewMeasurement>;

// Update payload. Each value field is nullable-optional with a three-way
// meaning the route honours (feedback_catalog_optional_field_must_always_send):
//   omitted   → leave the stored value alone
//   null      → CLEAR it (DDB REMOVE the attribute) — so a user who
//               backspaces a field out of an entry actually drops it from
//               history, and the per-field "latest" resolution falls back
//               to the previous reading
//   a number  → set it
// takenAt stays a plain optional (never cleared — it's the sk component;
// a change is a delete+recreate at the new sk, same as Session.startedAt).
export const PatchMeasurement = z.object({
  takenAt: z.iso.datetime().optional(),
  weightKg: WeightKg.nullable().optional(),
  heightCm: HeightCm.nullable().optional(),
  neckCm: Circumference.nullable().optional(),
  chestCm: Circumference.nullable().optional(),
  waistCm: Circumference.nullable().optional(),
  hipsCm: Circumference.nullable().optional(),
  bicepsLCm: Circumference.nullable().optional(),
  bicepsRCm: Circumference.nullable().optional(),
  forearmsLCm: Circumference.nullable().optional(),
  forearmsRCm: Circumference.nullable().optional(),
  thighsLCm: Circumference.nullable().optional(),
  thighsRCm: Circumference.nullable().optional(),
  calvesLCm: Circumference.nullable().optional(),
  calvesRCm: Circumference.nullable().optional(),
  bodyFatPct: BodyFatPct.nullable().optional(),
  leanMassKg: LeanMassKg.nullable().optional(),
  boneMassKg: BoneMassKg.nullable().optional(),
  bodyWaterKg: BodyWaterKg.nullable().optional(),
  bmrKcalDay: BmrKcalDay.nullable().optional(),
  note: z.string().max(500).nullable().optional(),
});
export type PatchMeasurement = z.infer<typeof PatchMeasurement>;
